import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ApiService } from 'src/app/shared/services/api.service';
import { AiBoqItem, AiBoqPreview, AiBoqService } from 'src/app/shared/services/ai-boq.service';
import { ToastrMsgService } from 'src/app/shared/services/shared-services/toastr.service';

@Component({
  selector: 'app-ai-boq',
  templateUrl: './ai-boq.component.html',
  styleUrls: ['./ai-boq.component.css']
})
export class AiBoqComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  projects: any[] = [];
  boqs: any[] = [];
  selectedProjectId: number | null = null;
  selectedBoqId: number | null = null;
  preview: AiBoqPreview | null = null;
  loading = false;
  saving = false;

  constructor(
    private api: ApiService,
    private aiBoq: AiBoqService,
    private toastr: ToastrMsgService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    const projectId = Number(this.route.snapshot.queryParamMap.get('project_id'));
    const boqId = Number(this.route.snapshot.queryParamMap.get('boq_id'));
    if (Number.isFinite(projectId) && projectId > 0) this.selectedProjectId = projectId;
    if (Number.isFinite(boqId) && boqId > 0) this.selectedBoqId = boqId;
    this.loadProjects();
    this.loadBoqs();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadProjects(): void {
    this.api.getProjectCommonAll({ limit: 1000 }).subscribe({
      next: (res: any) => {
        this.projects = res.data || res || [];
      },
      error: () => this.toastr.error('Failed to load projects')
    });
  }

  loadBoqs(): void {
    const params: any = { page: 1, limit: 200 };
    if (this.selectedProjectId) params.project_id = this.selectedProjectId;
    this.api.getBoQs(params).subscribe({
      next: (res: any) => {
        this.boqs = res.data || [];
      },
      error: () => this.toastr.error('Failed to load BoQs')
    });
  }

  onProjectChange(): void {
    this.preview = null;
    this.selectedBoqId = null;
    this.syncQuery();
    this.loadBoqs();
  }

  onBoqChange(): void {
    this.preview = null;
    this.syncQuery();
  }

  private syncQuery(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        project_id: this.selectedProjectId || null,
        boq_id: this.selectedBoqId || null
      },
      queryParamsHandling: 'merge'
    });
  }

  generate(): void {
    if (!this.selectedProjectId && !this.selectedBoqId) {
      this.toastr.warning('Select a project or an existing BoQ');
      return;
    }
    this.loading = true;
    this.preview = null;
    this.aiBoq.preview({
      project_id: this.selectedProjectId || undefined,
      boq_id: this.selectedBoqId || undefined
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        this.preview = res.data;
        if (res.data?.project?.id) {
          this.selectedProjectId = res.data.project.id;
        }
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        this.toastr.error(err.error?.message || 'Failed to generate preview');
      }
    });
  }

  sourceLabel(item: AiBoqItem): string {
    return item.source || 'template';
  }

  formatInr(value: number | null | undefined): string {
    const n = Number(value) || 0;
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(n);
  }

  get visibleRationale(): string[] {
    return (this.preview?.rationale || []).filter(
      (note) => !/AI provider is not configured/i.test(note) && !/AI suggestion was skipped/i.test(note)
    );
  }

  get aiStatus(): 'on' | 'off' | 'error' | null {
    if (!this.preview?.ai) return null;
    if (this.preview.ai.enabled) return 'on';
    if (this.preview.ai.reason === 'not_configured') return 'off';
    return 'error';
  }

  saveDraft(): void {
    if (!this.preview?.project?.id || !this.preview.items?.length) {
      this.toastr.warning('Generate a preview first');
      return;
    }
    if (!confirm('Save this proposal as a new draft BoQ? Existing BoQs will not be changed.')) {
      return;
    }
    this.saving = true;
    this.aiBoq.apply({
      project_id: this.preview.project.id,
      sections: this.preview.sections || [],
      items: this.preview.items,
      rationale: this.preview.rationale
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        this.saving = false;
        this.toastr.success(`Draft ${res.data.boq_code} created`);
        this.router.navigate(['/home/boq', res.data.boq_id, 'edit']);
      },
      error: (err) => {
        this.saving = false;
        this.toastr.error(err.error?.message || 'Failed to save draft BoQ');
      }
    });
  }
}
